import { useLazySession } from "@/app/layout/useLazySession";
import { Box, CircularProgress, styled, Typography } from "@mui/material";
import { ElementType } from "react";
import { GlobalNavMenu } from "./GlobalNavMenu";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { signOut } from "next-auth/react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AppendReturnUrl } from "@/services/AppendReturnUrl";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PasswordIcon from "@mui/icons-material/Password";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import LogoutIcon from "@mui/icons-material/Logout";
import { BREAKPOINT_NAV_MOBILE_MIN, BREAKPOINT_NAV_SUPER_COMPACT_MIN } from "@/app/layout/LayoutConstants";

interface MemberMenuBoxProps {
  component?: ElementType;
  href?: string;
  isMenuOpen?: boolean;
}

const MemberMenuBox = styled(Box, {
  shouldForwardProp: (prop) => {
    return prop !== "isMenuOpen";
  },
})<MemberMenuBoxProps>(({ theme, isMenuOpen }) =>
  theme.unstable_sx({
    display: "flex",
    alignItems: "center",
    gap: 1,
    height: "100%",
    padding: theme.spacing(2),
    [theme.breakpoints.down(BREAKPOINT_NAV_MOBILE_MIN)]: !isMenuOpen
      ? {
          padding: 1,
        }
      : {},
    ".large-only-or-menu-open": {
      overflow: "hidden",
      maxWidth: "150px",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      [theme.breakpoints.down("sm")]: !isMenuOpen
        ? {
            display: "none",
          }
        : {},
      [theme.breakpoints.down(BREAKPOINT_NAV_SUPER_COMPACT_MIN)]: !isMenuOpen
        ? {
            display: "none",
          }
        : {
            maxWidth: "100px",
          },
    },
  }),
);

export function GlobalMemberMenu({ isMenuOpen }: { isMenuOpen: boolean }) {
  const { session, isAuthenticated, isLoaded } = useLazySession();
  const pathname = usePathname();

  return (
    <GlobalNavMenu
      bindClick
      menu={
        isAuthenticated && isLoaded
          ? [
              { type: "divider", label: "Buy" },
              {
                type: "item",
                label: "Watch List",
                href: "/members/watchlist",
                icon: <BookmarksOutlinedIcon />,
              },
              { type: "divider", label: "Sell" },
              {
                type: "item",
                label: "Create Ad",
                href: "/sell",
                icon: <AddIcon />,
              },
              {
                type: "item",
                label: "Manage Ads",
                href: "/sell/dashboard",
                icon: <EditIcon />,
              },
              { type: "divider", label: "Account" },
              {
                type: "item",
                label: "Update Password",
                href: "/members/password",
                icon: <PasswordIcon />,
              },
              {
                type: "item",
                label: "Profile",
                href: "/members/profile",
                icon: <PermIdentityIcon />,
              },
              {
                type: "item",
                label: "Sign out",
                onClick: () => signOut(),
                icon: <LogoutIcon />,
              },
            ]
          : []
      }
      menuHeader={
        <Box
          sx={{
            color: "grey.500",
            paddingLeft: 2,
            paddingRight: 2,
          }}
        >
          <Typography variant="body2">
            Signed in as {session?.user?.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
            }}
          >
            {session?.user?.email}
          </Typography>
        </Box>
      }
    >
      {!isAuthenticated ? (
        <MemberMenuBox
          component={Link}
          href={AppendReturnUrl("/auth", pathname)}
          isMenuOpen={isMenuOpen}
        >
          {isLoaded ? (
            <PersonIcon />
          ) : (
            <CircularProgress size={24} color="inherit" />
          )}
          <Typography className="large-only-or-menu-open">Sign In</Typography>
        </MemberMenuBox>
      ) : (
        <MemberMenuBox isMenuOpen={isMenuOpen}>
          <PersonIcon />
          <Typography className="large-only-or-menu-open">
            {session?.user?.name || session?.user?.email || "Member"}
          </Typography>
          <ExpandMoreIcon className="large-only-or-menu-open" />
        </MemberMenuBox>
      )}
    </GlobalNavMenu>
  );
}
